// WhatsAppWidget.jsx - Widget flotante de WhatsApp para soporte
import React, { useState, useEffect } from 'react';
import { MessageCircle, X, Send, Clock } from 'lucide-react';

const WhatsAppWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [message, setMessage] = useState('');
  
  const whatsappNumber = '51997268543'; // Sin el +
  const defaultMessage = 'Hola, necesito información sobre MediExamPro';
  
  // Mostrar tooltip después de 5 segundos
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isOpen) {
        setShowTooltip(true);
        // Ocultar tooltip después de 5 segundos
        setTimeout(() => setShowTooltip(false), 5000);
      }
    }, 5000);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Verificar horario de atención
  const isBusinessHours = () => {
    const now = new Date();
    const day = now.getDay(); // 0 = Domingo, 6 = Sábado
    const hour = now.getHours();
    const minutes = now.getMinutes();
    const time = hour + minutes / 60;
    
    // Lunes a Viernes: 9:00 AM - 6:00 PM
    if (day >= 1 && day <= 5) {
      return time >= 9 && time < 18;
    }
    // Sábado: 9:00 AM - 1:00 PM
    if (day === 6) {
      return time >= 9 && time < 13;
    }
    // Domingo: Cerrado
    return false;
  };
  
  const handleSendMessage = () => {
    const finalMessage = message || defaultMessage;
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(finalMessage)}`;
    window.open(whatsappUrl, '_blank');
    setIsOpen(false);
    setMessage('');
  };
  
  const quickMessages = [
    'Información sobre planes y precios',
    'Quiero suscribirme al plan PRO',
    'Tengo problemas con mi cuenta',
    'Necesito ayuda con el pago',
    'Consulta sobre el contenido'
  ];
  
  const online = isBusinessHours();

  return (
    <>
      {/* Widget flotante */}
      <div className="fixed bottom-6 right-6 z-40">
        {/* Tooltip */}
        {showTooltip && !isOpen && (
          <div className="absolute bottom-16 right-0 bg-white rounded-lg shadow-xl p-3 w-64 animate-fade-in">
            <div className="relative">
              <button
                onClick={() => setShowTooltip(false)}
                className="absolute -top-1 -right-1 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
              <p className="text-sm text-gray-700 pr-4">
                ¡Hola! 👋 ¿Necesitas ayuda? Estamos aquí para ti
              </p>
              <div className="absolute bottom-0 right-8 transform translate-y-full">
                <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-white"></div>
              </div>
            </div>
          </div>
        )}
        
        {/* Chat expandido */}
        {isOpen && (
          <div className="absolute bottom-16 right-0 w-80 bg-white rounded-2xl shadow-2xl overflow-hidden animate-slide-up">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="h-10 w-10 bg-white/20 rounded-full flex items-center justify-center">
                      <MessageCircle className="h-6 w-6" />
                    </div>
                    <div className={`absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-green-500 ${
                      online ? 'bg-green-400' : 'bg-gray-400'
                    }`}></div>
                  </div>
                  <div>
                    <h3 className="font-semibold">MediExamPro</h3>
                    <p className="text-xs text-green-100">
                      {online ? 'En línea ahora' : 'Fuera de horario'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            {/* Horario de atención */}
            {!online && (
              <div className="bg-yellow-50 border-b border-yellow-200 p-3">
                <div className="flex items-center gap-2 text-sm text-yellow-800">
                  <Clock className="h-4 w-4" />
                  <div>
                    <p className="font-medium">Fuera de horario</p>
                    <p className="text-xs">L-V: 9AM-6PM | S: 9AM-1PM</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Contenido */}
            <div className="p-4">
              {/* Mensaje de bienvenida */}
              <div className="bg-gray-100 rounded-lg p-3 mb-4">
                <p className="text-sm text-gray-700">
                  👋 ¡Hola! Soy el equipo de soporte de MediExamPro. 
                  ¿En qué puedo ayudarte hoy?
                </p>
              </div>
              
              {/* Mensajes rápidos */}
              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-2">Mensajes rápidos:</p>
                <div className="space-y-2">
                  {quickMessages.map((quickMsg, index) => (
                    <button
                      key={index}
                      onClick={() => setMessage(quickMsg)}
                      className="w-full text-left text-sm bg-white border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50 hover:border-green-400 transition-colors"
                    >
                      {quickMsg}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Input de mensaje */}
              <div className="border-t pt-4">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder="Escribe tu mensaje..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <button
                    onClick={handleSendMessage}
                    className="bg-green-500 text-white p-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <Send className="h-5 w-5" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Serás redirigido a WhatsApp
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Botón principal */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`group relative bg-green-500 text-white p-4 rounded-full shadow-lg hover:bg-green-600 transition-all transform hover:scale-110 ${
            isOpen ? 'scale-0' : 'scale-100'
          }`}
        >
          <MessageCircle className="h-6 w-6" />
          {online && (
            <span className="absolute top-1 right-1 h-3 w-3 bg-green-400 rounded-full animate-ping"></span>
          )}
          <span className="absolute top-1 right-1 h-3 w-3 bg-green-400 rounded-full"></span>
        </button>
      </div>
      
      {/* Estilos de animación */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default WhatsAppWidget;